import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PreferencesList } from '@/components/PreferencesList';
import { NewClientBadge, ReferencePhotos } from '@/components/BookingExtras';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Calendar, Check, X, Search, Loader2, Phone, Clock, Edit, Trash2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBookingsSection = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [editBooking, setEditBooking] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientResults, setShowClientResults] = useState(false);
  const [deleteBooking, setDeleteBooking] = useState(null);
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchBookings(); fetchServices(); fetchClients(); }, []);
  useEffect(() => { filterBookings(); }, [bookings, searchTerm, activeTab]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, { headers: getAuthHeaders() });
      setBookings(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try { const r = await axios.get(`${API}/services`); setServices(r.data); } catch (e) { console.error('Failed to fetch services:', e); }
  };

  const fetchClients = async () => {
    try { const r = await axios.get(`${API}/clients`, { headers: getAuthHeaders() }); setClients(r.data); } catch (e) { console.error('Failed to fetch clients:', e); }
  };

  const filterBookings = () => {
    let filtered = bookings.filter(b => b.status === activeTab);
    if (searchTerm) {
      filtered = filtered.filter(b =>
        b.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.client_phone?.includes(searchTerm)
      );
    }
    filtered.sort((a, b) => new Date(`${b.date}T${b.time}`) - new Date(`${a.date}T${a.time}`));
    setFilteredBookings(filtered);
  };

  const handleUpdateStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success(newStatus === 'confirmed' ? 'Confermata!' : 'Annullata!');
      fetchBookings();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const openEditDialog = (booking) => {
    setEditBooking(booking);
    setEditForm({
      client_name: booking.client_name || '',
      client_phone: booking.client_phone || '',
      service_name: booking.service_name || '',
      date: booking.date || '',
      time: booking.time || '',
      status: booking.status || 'pending',
      notes: booking.notes || '',
    });
    setClientSearch('');
    setShowClientResults(false);
  };

  const handleSaveEdit = async () => {
    if (!editBooking) return;
    try {
      await axios.put(`${API}/bookings/${editBooking.id}`, editForm, { headers: getAuthHeaders() });
      toast.success('Prenotazione aggiornata!');
      setEditBooking(null);
      fetchBookings();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDeleteBooking = async () => {
    if (!deleteBooking) return;
    try {
      await axios.delete(`${API}/bookings/${deleteBooking.id}`, { headers: getAuthHeaders() });
      toast.success('Prenotazione eliminata!');
      setDeleteBooking(null);
      fetchBookings();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const filteredClients = clients.filter(c =>
    clientSearch.length >= 2 && (
      c.first_name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
      c.last_name?.toLowerCase().includes(clientSearch.toLowerCase())
    )
  );

  const selectClient = (client) => {
    setEditForm({
      ...editForm,
      client_name: `${client.first_name} ${client.last_name}`,
      client_phone: client.phone || '',
      client_id: client.id,
    });
    setClientSearch(`${client.first_name} ${client.last_name}`);
    setShowClientResults(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="bookings-section">
      <h2 className="text-xl sm:text-2xl font-bold">Gestione Prenotazioni</h2>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Cerca per nome o telefono..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 text-base" data-testid="bookings-search" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 h-auto">
          <TabsTrigger value="pending" className="text-xs sm:text-sm py-2" data-testid="bookings-tab-pending">
            Attesa ({bookings.filter(b => b.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="confirmed" className="text-xs sm:text-sm py-2" data-testid="bookings-tab-confirmed">
            Confermate ({bookings.filter(b => b.status === 'confirmed').length})
          </TabsTrigger>
          <TabsTrigger value="cancelled" className="text-xs sm:text-sm py-2" data-testid="bookings-tab-cancelled">
            Annullate ({bookings.filter(b => b.status === 'cancelled').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-3">
          {filteredBookings.length === 0 ? (
            <div className="text-center py-10">
              <Calendar className="h-10 w-10 text-muted-foreground mx-auto mb-2 opacity-50" />
              <p className="text-sm text-muted-foreground">Nessuna prenotazione</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredBookings.map((booking) => (
                <Card
                  key={booking.id}
                  className="card-elegant cursor-pointer hover:border-accent/30 transition-colors"
                  onClick={() => openEditDialog(booking)}
                  data-testid={`booking-${booking.id}`}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-sm sm:text-base truncate">{booking.client_name}</h3>
                          <NewClientBadge booking={booking} />
                          <Badge className={`text-[10px] px-1.5 py-0 ${
                            booking.status === 'confirmed' ? 'bg-accent/20 text-accent' :
                            booking.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                            'bg-destructive/20 text-destructive'
                          }`}>
                            {booking.status === 'confirmed' ? 'OK' : booking.status === 'pending' ? 'Attesa' : 'Ann.'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{booking.service_name}</p>
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{format(new Date(booking.date), 'EEE dd MMM', { locale: it })}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{booking.time}</span>
                          {booking.client_phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{booking.client_phone}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        {booking.status === 'pending' && (
                          <>
                            <Button size="sm" className="bg-accent text-accent-foreground h-8 w-8 p-0" onClick={() => handleUpdateStatus(booking.id, 'confirmed')} data-testid={`confirm-booking-${booking.id}`}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button variant="destructive" size="sm" className="h-8 w-8 p-0" onClick={() => handleUpdateStatus(booking.id, 'cancelled')} data-testid={`cancel-booking-${booking.id}`}>
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => openEditDialog(booking)} data-testid={`edit-booking-${booking.id}`}>
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30" onClick={() => setDeleteBooking(booking)} data-testid={`delete-booking-${booking.id}`}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Booking Dialog */}
      <Dialog open={!!editBooking} onOpenChange={(open) => { if (!open) setEditBooking(null); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica Prenotazione</DialogTitle></DialogHeader>
          {editBooking && (
            <div className="space-y-4">
              <NewClientBadge booking={editBooking} />
              <PreferencesList preferences={editBooking.client_preferences} testId="booking-detail-preferences" />
              <ReferencePhotos booking={editBooking} headers={getAuthHeaders()} />
              {/* Client search */}
              <div className="relative">
                <Label>Cerca Cliente Registrato</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={clientSearch}
                    onChange={(e) => { setClientSearch(e.target.value); setShowClientResults(true); }}
                    onFocus={() => setShowClientResults(true)}
                    placeholder="Cerca cliente..."
                    className="pl-10 text-base"
                    data-testid="edit-client-search"
                  />
                </div>
                {showClientResults && filteredClients.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 max-h-32 overflow-y-auto bg-card border border-border rounded-lg shadow-xl">
                    {filteredClients.map((c) => (
                      <button key={c.id} type="button" onClick={() => selectClient(c)}
                        className="w-full px-3 py-2 text-left hover:bg-secondary text-sm flex justify-between">
                        <span className="font-medium">{c.first_name} {c.last_name}</span>
                        <span className="text-xs text-muted-foreground">{c.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label>Nome Cliente *</Label>
                <Input value={editForm.client_name} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} className="text-base" data-testid="edit-booking-name" />
              </div>
              <div>
                <Label>Telefono</Label>
                <Input value={editForm.client_phone} onChange={(e) => setEditForm({ ...editForm, client_phone: e.target.value })} className="text-base" />
              </div>
              <div>
                <Label>Servizio *</Label>
                <Select value={editForm.service_name} onValueChange={(v) => setEditForm({ ...editForm, service_name: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (<SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Data</Label><Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="text-base" /></div>
                <div><Label>Ora</Label><Input type="time" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} className="text-base" /></div>
              </div>
              <div>
                <Label>Stato</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">In Attesa</SelectItem>
                    <SelectItem value="confirmed">Confermato</SelectItem>
                    <SelectItem value="cancelled">Annullato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note</Label>
                <Input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Note..." className="text-base" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditBooking(null)} className="flex-1">Annulla</Button>
                <Button onClick={handleSaveEdit} className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="save-edit-booking">Salva</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Booking Confirmation */}
      <AlertDialog open={!!deleteBooking} onOpenChange={(open) => { if (!open) setDeleteBooking(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa prenotazione?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare la prenotazione di <strong>{deleteBooking?.client_name}</strong> per {deleteBooking?.service_name} del {deleteBooking?.date} alle {deleteBooking?.time}. Questa azione non puo essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-booking">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBooking} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-booking">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminBookingsSection;
